import type { ChartPoint } from './HistoryChart';

// Evenly spaced tick timestamps across [tMin, tMax], inclusive of both ends.
// Used for the x-axis labels.
export function timeAxisTicks(tMin: number, tMax: number, count: number): number[] {
  if (count < 2 || tMax <= tMin) return [tMin];
  const step = (tMax - tMin) / (count - 1);
  return Array.from({ length: count }, (_, i) => tMin + i * step);
}

// Format a tick timestamp for the x-axis. Windows up to ~48h read better as
// HH:MM; longer spans fall back to D/M; spans beyond ~180 days need the year,
// because the Trends page's 1y range crosses a year boundary and bare D/M
// ticks there are ambiguous about which year they name. `spanMs` is
// tMax − tMin.
const DAY_MS = 24 * 60 * 60 * 1000;
export function formatAxisTime(t: number, spanMs: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (spanMs <= 48 * 60 * 60 * 1000) {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  if (spanMs > 180 * DAY_MS) {
    return `${d.getMonth() + 1}/${pad(d.getFullYear() % 100)}`;
  }
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

// Index of the point whose t is nearest the target; -1 for empty data.
// Used for hover hit-testing.
export function nearestIndexByT(data: ChartPoint[], target: number): number {
  if (data.length === 0) return -1;
  let best = 0;
  let bestDist = Math.abs(data[0]!.t - target);
  for (let i = 1; i < data.length; i++) {
    const dist = Math.abs(data[i]!.t - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

// Arithmetic mean of a series' values, ignoring non-finite readings. Returns
// null when there's nothing to average, so callers can skip the ref line
// rather than draw one at NaN.
export function mean(data: ChartPoint[]): number | null {
  let sum = 0;
  let n = 0;
  for (const p of data) {
    if (Number.isFinite(p.v)) { sum += p.v; n++; }
  }
  return n === 0 ? null : sum / n;
}
